import React, { useCallback, useEffect, useState } from "react";
import { C } from "../../core/constants";
import { APP_FONT_SIZE } from "../../core/theme";
import { normalizePathname } from "../../core/navigation";
import { localizedPath, stripViPrefix } from "../../core/localePaths";
import { useI18n } from "../i18n/LocaleProvider";
import { signIn, signUp } from "./api";
import { clearRememberedAuth, readRememberedAuth, writeRememberedAuth } from "./storage";
import GlassCard from "../../ui/GlassCard";
import { applyAuthSession } from "../../config/runtime";
import { gaSetUserFromAuth, gaTrackEvent } from "../../analytics/ga4";

function readReturnTo() {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("return_to");
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

function readPageFlash() {
  if (typeof window === "undefined") return null;
  const raw = window.PAGE_FLASH;
  if (!raw || typeof raw !== "object") return null;
  const notice = raw.notice;
  const alert = raw.alert;
  const alert_key = typeof raw.alert_key === "string" ? raw.alert_key.trim() : "";
  if (!notice && !alert && !alert_key) return null;
  return { notice, alert, alert_key };
}

export default function LoginPage({ setPage, acc, t: tProp }) {
  const { t: tContext, locale } = useI18n();
  const t = tProp || tContext;
  const fs = APP_FONT_SIZE;
  const rememberedCreds = readRememberedAuth();
  const [mode, setMode] = useState(() => {
    const fromWindow = window.INITIAL_AUTH_MODE;
    if (fromWindow === "signup" || fromWindow === "signin") return fromWindow;
    return "signin";
  }); // signin | signup
  const [email, setEmail] = useState(rememberedCreds.email || "");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [rememberCreds, setRememberCreds] = useState(Boolean(rememberedCreds.email));
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pageFlash] = useState(readPageFlash);
  const [loading, setLoading] = useState(false);
  /** Hide server flash (e.g. "Please sign…") once the user engages with auth UI. */
  const [serverFlashDismissed, setServerFlashDismissed] = useState(false);
  /** Defer past the full click/pointer sequence so hiding the banner does not reflow before the target `click` fires. */
  const dismissServerFlashBanner = useCallback(() => {
    window.setTimeout(() => setServerFlashDismissed(true), 0);
  }, []);

  /** Same path as the footer “Sign up free” / “Sign in” link: `click` only (tabs sit outside capture wrapper so they must dismiss here). */
  const applyAuthMode = useCallback((nextMode) => {
    if (nextMode !== "signup" && nextMode !== "signin") return;
    setMode(nextMode);
    const path = nextMode === "signup" ? localizedPath("signUp", locale) : localizedPath("login", locale);
    window.history.replaceState({}, "", path);
    setServerFlashDismissed(true);
  }, [locale]);

  /** Server `flash[:alert]` is fixed at render time; keyed alerts resolve via `t()` so locale switches work. */
  const bannerAlert =
    typeof pageFlash?.alert_key === "string" && pageFlash.alert_key
      ? t(pageFlash.alert_key)
      : pageFlash?.alert || "";

  const showNoticeFlash = Boolean(!serverFlashDismissed && pageFlash?.notice);
  const showAlertFlash = Boolean(!serverFlashDismissed && bannerAlert);

  const startGoogleAuth = useCallback(async () => {
    let token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || "";
    try {
      // Back/forward cache can serve a stale token after session reset.
      const response = await fetch(localizedPath("login", locale), {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
      });
      const html = await response.text();
      const match = html.match(/<meta[^>]*name=["']csrf-token["'][^>]*content=["']([^"']+)["']/i);
      if (match?.[1]) {
        token = match[1];
        const currentMeta = document.querySelector("meta[name='csrf-token']");
        if (currentMeta) currentMeta.setAttribute("content", token);
      }
    } catch (_) {
      // Fall back to the current page token if refresh fails.
    }

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/auth/google_oauth2";

    if (token) {
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = token;
      form.appendChild(tokenInput);
    }

    document.body.appendChild(form);
    form.submit();
  }, [locale]);

  useEffect(() => {
    const syncAuthModeFromUrl = () => {
      const raw = stripViPrefix(normalizePathname(window.location.pathname));
      if (raw === "/sign-up") setMode("signup");
      else if (raw === "/sign-in") setMode("signin");
    };
    window.addEventListener("popstate", syncAuthModeFromUrl);
    syncAuthModeFromUrl();
    return () => window.removeEventListener("popstate", syncAuthModeFromUrl);
  }, []);

  useEffect(() => {
    setError("");
    setNotice("");
    if (mode === "signup") {
      setEmail("");
      setPassword("");
      setPasswordConfirmation("");
      setShowPassword(false);
      setShowPasswordConfirmation(false);
      setBirthDate("");
      setFullName("");
      return;
    }

    if (rememberCreds) {
      const stored = readRememberedAuth();
      setEmail(stored.email || "");
      setPassword("");
      setShowPassword(false);
      setShowPasswordConfirmation(false);
    }
  }, [mode]);

  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 12, color: C.textPrimary, padding: "13px 16px",
    fontSize: fs - 1, fontFamily: "'DM Sans',sans-serif", outline: "none",
    transition: "border 0.2s",
  };

  const submitAuth = useCallback(async () => {
    setError("");
    setNotice("");
    setLoading(true);

    try {
      if (mode === "signin") {
        const returnTo = readReturnTo();
        const payload = await signIn({ email, password, ...(returnTo ? { return_to: returnTo } : {}) });
        const authContext = applyAuthSession(payload);
        gaSetUserFromAuth(authContext);
        gaTrackEvent("login", { method: "email" });
        if (rememberCreds) {
          writeRememberedAuth({ email });
        } else {
          clearRememberedAuth();
        }
        const redirectAfterLogin = payload?.return_to || payload?.["return_to"];
        if (redirectAfterLogin) {
          window.location.assign(redirectAfterLogin);
          return;
        }
        setPage("dashboard");
      } else {
        await signUp({
          user: {
            email,
            fullname: fullName,
            password,
            password_confirmation: passwordConfirmation,
            birth_date: birthDate,
          },
        });
        gaTrackEvent("sign_up", { method: "email" });
        window.location.assign(localizedPath("emailVerificationRequired", locale));
      }
    } catch (e) {
      if (e?.code === "email_verification_required") {
        window.location.assign(e.redirectTo || localizedPath("emailVerificationRequired", locale));
        return;
      }
      setError(e.message || t("auth_error_unable_to_continue"));
    } finally {
      setLoading(false);
    }
  }, [mode, email, fullName, password, passwordConfirmation, birthDate, rememberCreds, setPage, locale, t]);

  const handleAuthSubmit = useCallback((event) => {
    event.preventDefault();
    if (loading) return;
    if (mode === "signin" && (!email.trim() || !password)) return;
    submitAuth();
  }, [mode, loading, email, password, submitAuth]);

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", zIndex: 1, padding: 24,
    }}>
      <div style={{ position: "fixed", inset: 0, background: `radial-gradient(ellipse 60% 70% at 50% 50%, ${acc.primary}0d 0%, transparent 70%)`, pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 420, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, color: acc.primary, marginBottom: 6 }}>
            ✦ Ecliptica
          </div>
          <div style={{ fontSize: 12, letterSpacing: "0.18em", color: C.textMuted, textTransform: "uppercase" }}>{t("auth_brand_subtitle")}</div>
        </div>

        <GlassCard style={{ padding: 40 }} glow accent={acc.primary}>
          {(showNoticeFlash || showAlertFlash) && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 24,
              }}
            >
              {showNoticeFlash ? (
                <div
                  role="status"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid rgba(100, 200, 160, 0.35)",
                    background: "rgba(100, 200, 160, 0.12)",
                    color: C.textPrimary,
                    fontSize: 14,
                    lineHeight: 1.45,
                    textAlign: "left",
                  }}
                >
                  {pageFlash.notice}
                </div>
              ) : null}
              {showAlertFlash ? (
                <div
                  role="alert"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid rgba(239, 120, 120, 0.45)",
                    background: "rgba(239, 80, 100, 0.1)",
                    color: C.textPrimary,
                    fontSize: 14,
                    lineHeight: 1.45,
                    textAlign: "left",
                  }}
                >
                  {bannerAlert}
                </div>
              ) : null}
            </div>
          )}

          <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: 4, marginBottom: 32 }}>
            {[["signin", t("sign_in")], ["signup", t("auth_mode_create_account")]].map(([m, label]) => (
              <button
                key={m}
                type="button"
                onMouseDown={(e) => {
                  /** Match non-button controls: avoid focus flicker interacting with flash/capture semantics. */
                  e.preventDefault();
                }}
                onClick={() => applyAuthMode(m)}
                style={{
                  flex: 1, padding: "9px", borderRadius: 9, border: "none", cursor: "pointer",
                  background: mode === m ? `linear-gradient(135deg,${acc.primary}99,${acc.tertiary}88)` : "transparent",
                  color: mode === m ? "#fff" : C.textMuted,
                  fontFamily: "'DM Sans',sans-serif", fontWeight: 500, fontSize: 14,
                  boxShadow: mode === m ? `0 0 20px ${acc.primary}44` : "none",
                  transition: "all 0.25s",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            role="presentation"
            onPointerDownCapture={dismissServerFlashBanner}
            onFocusCapture={dismissServerFlashBanner}
          >
          <form onSubmit={handleAuthSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {mode === "signup" && (
                <div>
                  <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("full_name").toUpperCase()}</div>
                  <input
                    style={inputStyle}
                    type="text"
                    name="auth_fullname"
                    autoComplete="name"
                    placeholder={t("auth_full_name_placeholder")}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("auth_label_email_upper")}</div>
                <input
                  style={inputStyle}
                  type="email"
                  name="auth_email"
                  autoComplete="off"
                  placeholder={t("auth_placeholder_email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em" }}>{t("auth_label_password_upper")}</div>
                  {mode === "signin" && <span style={{ fontSize: 12, color: acc.tertiary, cursor: "pointer" }}>{t("auth_forgot_password")}</span>}
                </div>
                <div style={{ position: "relative" }}>
                <input
                  style={{ ...inputStyle, paddingRight: 44 }}
                  type={showPassword ? "text" : "password"}
                  name="auth_password"
                  autoComplete="new-password"
                  placeholder={t("auth_placeholder_password_hidden")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? t("auth_hide_password") : t("auth_show_password")}
                    title={showPassword ? t("auth_hide_password") : t("auth_show_password")}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      color: C.textMuted,
                      cursor: "pointer",
                      fontSize: 16,
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    <img
                      src={showPassword ? "/icons/auth-eye-off.svg" : "/icons/auth-eye.svg"}
                      width="16"
                      height="16"
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
              {mode === "signup" && (
                <div>
                  <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("auth_label_confirm_password_upper")}</div>
                  <div style={{ position: "relative" }}>
                  <input
                    style={{ ...inputStyle, paddingRight: 44 }}
                    type={showPasswordConfirmation ? "text" : "password"}
                    name="auth_password_confirmation"
                    autoComplete="new-password"
                    placeholder={t("auth_placeholder_password_hidden")}
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                  />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                      aria-label={showPasswordConfirmation ? t("auth_hide_password") : t("auth_show_password")}
                      title={showPasswordConfirmation ? t("auth_hide_password") : t("auth_show_password")}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        color: C.textMuted,
                        cursor: "pointer",
                        fontSize: 16,
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      <img
                        src={showPasswordConfirmation ? "/icons/auth-eye-off.svg" : "/icons/auth-eye.svg"}
                        width="16"
                        height="16"
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              )}
              {mode === "signup" && (
                <div>
                  <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("auth_label_birthday_upper")}</div>
                  <input
                    style={inputStyle}
                    type="date"
                    name="auth_birth_date"
                    autoComplete="bday"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>
              )}
            </div>

            {mode === "signin" && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12, color: C.textSecondary }}>
                <input
                  type="checkbox"
                  checked={rememberCreds}
                  onChange={(e) => setRememberCreds(e.target.checked)}
                />
                {t("auth_remember_email")}
              </label>
            )}

            {error && <div style={{ marginTop: 14, fontSize: 12, color: "#ff9ca8" }}>{error}</div>}
            {notice && <div style={{ marginTop: 14, fontSize: 12, color: acc.primary }}>{notice}</div>}

            <button type="submit" disabled={loading} style={{
              width: "100%", marginTop: 24, padding: "14px",
              borderRadius: 12, border: "none", cursor: "pointer",
              background: `linear-gradient(135deg,${acc.primary},${acc.tertiary})`,
              color: "#fff", fontFamily: "'DM Sans',sans-serif",
              fontWeight: 600, fontSize: fs, letterSpacing: "0.02em",
              boxShadow: `0 0 30px ${acc.primary}55`,
              opacity: loading ? 0.7 : 1,
            }}>
              {loading ? t("auth_loading_wait") : (mode === "signin" ? t("auth_submit_sign_in") : t("auth_submit_sign_up"))}
            </button>
          </form>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
            <span style={{ fontSize: 12, color: C.textMuted }}>{t("auth_continue_with")}</span>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={startGoogleAuth}
              style={{
                flex: 1,
                padding: "11px 8px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.09)",
                background: "rgba(255,255,255,0.03)",
                color: C.textSecondary,
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                transition: "all 0.2s",
              }}
            >
              <img src="/icons/google-g.svg" width="18" height="18" alt="" aria-hidden="true" />
              <span>{t("auth_provider_google")}</span>
            </button>
          </div>

          <p style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: C.textMuted }}>
            {mode === "signin" ? t("auth_no_account") : t("auth_already_member")}
            <span style={{ color: acc.primary, cursor: "pointer" }} onClick={() => applyAuthMode(mode === "signin" ? "signup" : "signin")}>
              {mode === "signin" ? t("auth_sign_up_free") : t("sign_in")}
            </span>
          </p>
          </div>
        </GlassCard>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: C.textMuted, lineHeight: 1.6 }}>
          {t("auth_agree_prefix")}{" "}
          <a href={localizedPath("terms", locale)} style={{ color: acc.primary, textDecoration: "none" }}>{t("terms_of_service")}</a>
          {" "}{t("and")}{" "}
          <a href={localizedPath("privacy", locale)} style={{ color: acc.primary, textDecoration: "none" }}>{t("privacy_policy")}</a>
        </p>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <span style={{ fontSize: 13, color: C.textMuted, cursor: "pointer" }} onClick={() => setPage("landing")}>{t("auth_back_to_home")}</span>
        </div>
      </div>
    </div>
  );
}
